// One Album's detail hook: loads its AlbumView via `GET /api/albums/:id` (ADR-0059) and
// removes an item (`POST /api/albums/:id/items/remove`), reflecting the change in place.
// Thin over the `api` client (request shaping + error normalization live there); this
// sequences the calls + holds React state, so it is unit-testable with a stub client
// (like `useDevices` / `useAlbums`). (Reorder dropped with the detail page's up/down
// arrows — the endpoint + client.reorderAlbum remain for a future drag-reorder.)
//
// Remove updates the local `items` OPTIMISTICALLY (instant feedback), then fires the
// write; a failure rolls back (the server stays the source of truth) so a rejected
// mutation can't leave the grid out of sync. The id is a hook dependency, so navigating
// between two albums re-loads cleanly.
//
// Publishing (ADR-0061): the publish-copy behind Sharing runs on a queue, so the album's
// `publishState` changes on the SERVER with no request from us. While it reads "publishing"
// this hook polls the same `getAlbum` path on an interval and applies the result QUIETLY
// (`refresh` — no `loading` flip, so the detail view never blinks back to a spinner). The
// interval is cleared on unmount and the moment publishing stops — and it also STOPS ITSELF
// on an auth failure or after a run of consecutive failures, so an expired Session can't leave
// a background tab retrying a 401 forever.
//
// Two request ids, deliberately: `reqId` is the supersession guard (only the NEWEST request may
// write `album`), while `loadingOwner` records which request turned the spinner ON. They must be
// separate — a quiet poll bumps `reqId` without owning the spinner, so keying the `loading`
// release off `reqId` stranded `loading: true` forever whenever a poll overlapped a real load.

import { useCallback, useEffect, useRef, useState } from "react";
import { type ApiClient, type AlbumView, ApiError } from "./api";

export interface UseAlbumState {
  album: AlbumView | null;
  /** True during the first detail load (drives the initial spinner). */
  loading: boolean;
  /** A non-auth error message to surface (auth → `unauthorized`). */
  error: string | null;
  /** The Owner Session is missing/expired — show the re-auth affordance. */
  unauthorized: boolean;
  /** A remove is in flight — disable the mutating controls. */
  busy: boolean;
  /** Remove one item (by key) from the album. */
  removeItem: (key: string) => Promise<void>;
  /** Reload the album from scratch (shows the loading state). */
  reload: () => void;
  /** Re-read the album QUIETLY — no `loading` flip, so the view doesn't blink (used by the
   *  publish poll + the share actions, which shouldn't tear down the screen they act on). */
  refresh: () => Promise<void>;
}

/** How often to re-read an album whose publish-copy is still running (ADR-0061). */
const PUBLISH_POLL_MS = 2500;

/** Give up polling after this many CONSECUTIVE failed re-reads (an auth failure stops at once). */
const PUBLISH_POLL_MAX_FAILURES = 3;

/** What one `load` did: whether it owned the outcome, and how it ended (drives the poll's stop). */
type LoadOutcome = "ok" | "error" | "unauthorized" | "superseded";

/** Manage one Album's detail view + the remove action for the album detail page. */
export function useAlbum(client: ApiClient, id: string): UseAlbumState {
  const [album, setAlbum] = useState<AlbumView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [busy, setBusy] = useState(false);

  // Invalidate an in-flight load when a reload (or an id change) supersedes it.
  const reqId = useRef(0);
  // Which request currently OWNS the `loading` flag (0 = nobody). Only that request may clear it,
  // and only a newer non-quiet load may take ownership — so the spinner is always released by
  // whoever raised it, even when a quiet poll bumped `reqId` underneath it.
  const loadingOwner = useRef(0);

  // `quiet` skips the loading flip: the caller already has an album on screen and only wants
  // the fresh copy swapped in underneath it (the publish poll, and the share actions).
  const load = useCallback(
    async (quiet = false): Promise<LoadOutcome> => {
      const rid = ++reqId.current;
      if (!quiet) {
        loadingOwner.current = rid;
        setLoading(true);
      }
      setError(null);
      try {
        const view = await client.getAlbum(id);
        // A superseded load must never overwrite the newer request's album data.
        if (rid !== reqId.current) return "superseded";
        setAlbum(view);
        return "ok";
      } catch (err) {
        if (rid !== reqId.current) return "superseded";
        if (err instanceof ApiError && err.isUnauthorized) {
          setUnauthorized(true);
          return "unauthorized";
        }
        setError(err instanceof Error ? err.message : "failed to load album");
        return "error";
      } finally {
        // Release the spinner iff THIS request raised it — independent of `reqId`, which a quiet
        // poll may have bumped in the meantime (that bug stranded the screen on "Loading album…").
        if (!quiet && loadingOwner.current === rid) {
          loadingOwner.current = 0;
          setLoading(false);
        }
      }
    },
    [client, id],
  );

  const removeItem = useCallback(
    async (key: string): Promise<void> => {
      if (busy) return;
      setBusy(true);
      setError(null);
      // Optimistic: drop the card immediately, then persist.
      const prev = album;
      setAlbum((a) =>
        a
          ? { ...a, items: a.items.filter((it) => it.key !== key), itemCount: Math.max(0, a.itemCount - 1) }
          : a,
      );
      try {
        await client.removeFromAlbum(id, key);
      } catch (err) {
        setAlbum(prev); // roll back the optimistic drop
        if (err instanceof ApiError && err.isUnauthorized) {
          setUnauthorized(true);
        } else {
          setError(err instanceof Error ? err.message : "failed to remove item");
        }
      } finally {
        setBusy(false);
      }
    },
    [client, id, album, busy],
  );

  const reload = useCallback(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async (): Promise<void> => {
    await load(true);
  }, [load]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id]);

  // Publish poll (ADR-0061): the queue consumer moves `publishState` with no request from us,
  // so while it reads "publishing" re-read the album quietly until it lands on idle/failed.
  // It also has to STOP ON ITS OWN: an expired Session would otherwise 401 every 2.5 s for as long
  // as the tab stays open, so an auth failure halts immediately and a run of consecutive transport
  // failures halts too (the screen keeps whatever it last had; Retry/reload restarts the cycle).
  const publishing = album?.publishState === "publishing";
  useEffect(() => {
    if (!publishing) return;
    let stopped = false;
    let failures = 0;
    const t = setInterval(() => {
      void (async () => {
        if (stopped) return;
        const outcome = await load(true);
        if (stopped) return;
        if (outcome === "unauthorized") {
          stopped = true;
          clearInterval(t);
        } else if (outcome === "error") {
          failures += 1;
          if (failures >= PUBLISH_POLL_MAX_FAILURES) {
            stopped = true;
            clearInterval(t);
          }
        } else if (outcome === "ok") {
          failures = 0; // a good read clears the streak
        }
      })();
    }, PUBLISH_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [publishing, load]);

  return { album, loading, error, unauthorized, busy, removeItem, reload, refresh };
}
